import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { OfflineProvider } from "./context/OfflineContext";
import { CalendarReminderProvider } from "./context/CalendarReminderContext";
import { Layout } from "./components/Layout";
import { UpdateBanner } from "./components/UpdateBanner";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CustomersPage } from "./pages/CustomersPage";
import { OrdersPage } from "./pages/OrdersPage";
import { NewOrderPage } from "./pages/NewOrderPage";
import { OrderDetailPage } from "./pages/OrderDetailPage";
import { EmployeeOrdersPage } from "./pages/EmployeeOrdersPage";
import { EmployeeOrderDetailPage } from "./pages/EmployeeOrderDetailPage";
import { EmployeeCalendarPage } from "./pages/EmployeeCalendarPage";
import { ProfilePage } from "./pages/ProfilePage";
import { TimeTrackingPage } from "./pages/TimeTrackingPage";
import { VacationPlannerPage } from "./pages/VacationPlannerPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import { AdminManagementPage } from "./pages/AdminManagementPage";
import { AdminTimeManagementPage } from "./pages/AdminTimeManagementPage";
import { LiveTrackingPage } from "./pages/LiveTrackingPage";
import { MorePage } from "./pages/MorePage";
import { SetupPage } from "./pages/SetupPage";
import { SettingsPage } from "./pages/SettingsPage";
import { IrrigationPage } from "./pages/IrrigationPage";
import { TerminalPage } from "./pages/TerminalPage";
import PayrollPage from "./pages/PayrollPage";

// Check if employee setup is complete
const isSetupComplete = (userId) => {
    return localStorage.getItem(`setup_complete_${userId}`) === 'true';
};

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false, skipSetupCheck = false }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    if (adminOnly && user.role !== 'ADMIN') {
        return <Navigate to="/my-orders" replace />;
    }
    
    // For employees: check if setup is complete (unless we're on the setup page or it's an admin)
    if (!skipSetupCheck && user.role === 'EMPLOYEE' && !isSetupComplete(user.id)) {
        return <Navigate to="/setup" replace />;
    }
    
    return children;
};

// Home redirect based on role
const HomeRedirect = () => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    // Employees need setup check
    if (user.role === 'EMPLOYEE' && !isSetupComplete(user.id)) {
        return <Navigate to="/setup" replace />;
    }
    
    return <Navigate to={user.role === 'ADMIN' ? '/dashboard' : '/my-orders'} replace />;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/terminal" element={<TerminalPage />} />
            <Route path="/" element={<HomeRedirect />} />
            
            {/* Setup Route - Employee first-time setup (skipSetupCheck to avoid redirect loop) */}
            <Route path="/setup" element={<ProtectedRoute skipSetupCheck><SetupPage /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route element={<ProtectedRoute adminOnly><Layout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/orders/new" element={<NewOrderPage />} />
                <Route path="/orders/:id" element={<OrderDetailPage />} />
                <Route path="/payroll" element={<PayrollPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/admins" element={<AdminManagementPage />} />
                <Route path="/time-management" element={<AdminTimeManagementPage />} />
                <Route path="/live-tracking" element={<LiveTrackingPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/more" element={<MorePage />} />
                <Route path="/irrigation" element={<IrrigationPage />} />
            </Route>
            
            {/* Shared Routes (Admin and Employee) */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/vacation" element={<VacationPlannerPage />} />
                <Route path="/profile" element={<ProfilePage />} />
            </Route>
            
            {/* Employee Routes */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/my-orders" element={<EmployeeOrdersPage />} />
                <Route path="/my-orders/:id" element={<EmployeeOrderDetailPage />} />
                <Route path="/calendar" element={<EmployeeCalendarPage />} />
                <Route path="/time-tracking" element={<TimeTrackingPage />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<HomeRedirect />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <OfflineProvider>
                <CalendarReminderProvider>
                    <BrowserRouter>
                        <UpdateBanner />
                        <AppRoutes />
                        <Toaster position="top-center" richColors />
                    </BrowserRouter>
                </CalendarReminderProvider>
            </OfflineProvider>
        </AuthProvider>
    );
}

export default App;
